import { Back, Linear, Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GameType } from "../Data/GameData";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import { Button } from "../GameObjects/Button";
import { Paintable } from "../GameObjects/Paintable";
import { PaintPalette } from "../GameObjects/PaintPalette";
import { ZoomOnboarding } from "../GameObjects/ZoomOnboarding";
import { Game } from "../States/Game";

export abstract class GamePaintable extends Game {
    protected static readonly MIN_SCALE_DIRECTION_FRAMES: number = 45;
    protected static readonly TIME_BETWEEN_ZOOM_SFX: number = 0.5;
    protected static readonly MAX_SCALE: number = 3.0;
    protected static readonly PINCH_MAX_DELTA: number = 200;

    protected _palette: PaintPalette;
    protected _paintables: Paintable[];
    protected _activePaintableIndex: number;
    protected _buttonNext: Button;
    protected _buttonPrev: Button;
    protected _touchManager: HLF.Input.TouchManager;
    protected _zoomOnboarding: ZoomOnboarding;

    private _hasZoomed: boolean ;
    private _pinchDelta: number;
    private _zoomPerc: number;
    private _paintableSize!: PIXI.Point;

    constructor() {
        super();
        PubSub.publishSync(HLF.SET_KEYBOARD_AUTOMAP, false);
        this._activePaintableIndex = 0;
        this._pinchDelta = 0;
        this._zoomPerc = 0;
        this._hasZoomed = false;
        this._paintables = new Array<Paintable>();
        this._paintableSize = new PIXI.Point();

        this._palette = new PaintPalette();
        this.addChild(this._palette);

        this._buttonNext = new Button(
            this.onNextPressed.bind(this),
            "button-right", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this.addChild(this._buttonNext);

        this._buttonPrev = new Button(
            this.onPrevPressed.bind(this),
            "button-left", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this.addChild(this._buttonPrev);

        this._zoomOnboarding = new ZoomOnboarding();
        this.addChild(this._zoomOnboarding);

        this._touchManager = new HLF.Input.TouchManager(
            Application.instance.renderer.plugins.interaction,
            Application.instance.stage,
        );
        this._touchManager.onSwipeStart = this.onTouchStart.bind(this);
        this._touchManager.onSwipeEnd = this.onTouchEnd.bind(this);
        this._touchManager.onPinchStart = this.onTouchStart.bind(this);
        this._touchManager.onPinchEnd = this.onTouchEnd.bind(this);
        this._touchManager.onSwipe = this.onSwipe.bind(this);
        this._touchManager.onPinch = this.onPinch.bind(this);
        this._touchManager.swipeThreshhold = 25;
        this._touchManager.pinchThreshhold = 15;

        this._pubSubTokens.push(PubSub.subscribe(
            Topics.ACTIVE_PAINT_SELECTED,
            this.onPaintPaletteColourChanged.bind(this),
        ));
        this._pubSubTokens.push(PubSub.subscribe(
            Topics.ACTIVE_PAINT_DESELECTED,
            this.onPaintPaletteColourChanged.bind(this),
        ));
        this._pubSubTokens.push(PubSub.subscribe(
            Topics.KEYBOARD_REMAP,
            this.createKeyboardMap.bind(this),
        ));
    }

    protected get activePaintable(): Paintable {
        return this._paintables[this._activePaintableIndex];
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        this._touchManager.destroy();
        for (let i = 0; i < this._pubSubTokens.length; ++i) {
            PubSub.unsubscribe(this._pubSubTokens[i]);
        }
        super.destroy(pOptions);
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);
        this._palette.position.set(-this._size.x * 0.5 + 65, 0);
        this._zoomOnboarding.onResize(pSize);

        this._buttonClear.position.set(
            this._size.x * 0.5 + (this._buttonClear.interactive ? -1 : 1) * (this._buttonClear.width * 0.5 + 15),
            -241,
        );
        this._buttonNext.position.set(
            this._size.x * 0.5 + (this._buttonNext.interactive ? -1 : 1) * (this._buttonNext.width * 0.5 + 15),
            -133,
        );
        this._buttonPrev.position.set(
            this._size.x * 0.5 + (this._buttonPrev.interactive ? -1 : 1) * (this._buttonPrev.width * 0.5 + 15),
            -25,
        );
        this._buttonSubmit.position.y = 100;
    }

    public animateIn(pOnComplete: () => void): void {
        super.animateIn(pOnComplete);
        this.setButtonsInteractive(false);
        this.setTouchControlsEnabled(false);

        this._palette.position.x = -this._size.x * 0.5 - this._palette.width;
        this._buttonClear.position.x = this._size.x * 0.5 + this._buttonClear.width;
        this._buttonNext.position.x = this._size.x * 0.5 + this._buttonNext.width;
        this._buttonPrev.position.x = this._size.x * 0.5 + this._buttonPrev.width;
        this._buttonSubmit.position.x = this._size.x * 0.5 + this._buttonSubmit.width;
        this.activePaintable.position.set(
            -this._size.x * 0.5 - this.activePaintable.width,
            0,
        );
        this.activePaintable.setInputEnabled(false);

        const timeline: TimelineLite = new TimelineLite();

        timeline.to(this.activePaintable.position, 1.0, {
            x: 0,
            ease: Sine.easeOut,
        }, 0.5);

        timeline.to(this._palette.position, 0.5, {
            x: -this._size.x * 0.5 + 65,
            ease: Sine.easeOut,
        }, 1.0);
        if (this._isOnboarding === false) {
            timeline.to(this._buttonClear.position, 0.5, {
                x: this._size.x * 0.5 - this._buttonClear.width * 0.5 - 15,
                ease: Back.easeOut,
            }, 1.0);
            timeline.to(this._buttonNext.position, 0.5, {
                x: this._size.x * 0.5 - this._buttonNext.width * 0.5 - 15,
                ease: Back.easeOut,
            }, 1.1);
            timeline.to(this._buttonPrev.position, 0.5, {
                x: this._size.x * 0.5 - this._buttonPrev.width * 0.5 - 15,
                ease: Back.easeOut,
            }, 1.2);
            timeline.to(this._buttonSubmit.position, 0.5, {
                x: this._size.x * 0.5 - this._buttonSubmit.width * 0.5 - 15,
                ease: Back.easeOut,
            }, 1.3);
        }
        timeline.call(this.onAnimateInComplete.bind(this), undefined, this, timeline.duration() + 0.1);
    }

    public animateOut(pOnComplete: () => void): void {
        super.animateOut(pOnComplete);

        this.setButtonsInteractive(false);
        this.setTouchControlsEnabled(false);
        this.resetZoom();

        const timeline: TimelineLite = new TimelineLite();
        // Tween out palette
        timeline.to(this._palette.position, 0.5, {
            x: -this._size.x * 0.5 - this._palette.width,
            ease: Sine.easeIn,
        }, 0);
        // Tween out buttons
        timeline.to(this._buttonClear.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonClear.width,
            ease: Sine.easeIn,
        }, 0);
        timeline.to(this._buttonNext.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonNext.width,
            ease: Sine.easeIn,
        }, 0.1);
        timeline.to(this._buttonPrev.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonPrev.width,
            ease: Sine.easeIn,
        }, 0.2);
        timeline.to(this._buttonSubmit.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonSubmit.width,
            ease: Sine.easeIn,
        }, 0.3);
        // Tween out paintable
        timeline.call(() => {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                SoundEffect.LIFT_ART,
                1, false, HLF.AudioCategory.SFX,
            ));
        }, undefined, undefined, 0.5);
        timeline.to(this.activePaintable.position, 0.8, {
            x: -this._size.x * 0.5 - this._paintables[0].width,
            ease: Sine.easeIn,
        }, 0.5);
    }

    protected onAnimateInComplete(): void {
        super.onAnimateInComplete();
        PubSub.publishSync(Topics.PAINTABLE_GAME_ENTERED, undefined);
        this.setTouchControlsEnabled(true);
        this._paintableSize.set(this.activePaintable.width, this.activePaintable.height);

        if (this._isOnboarding) {
            this.populateOnboarding();
        }
        else {
            this.setButtonsInteractive(true);
            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                this._buttonClear, this._buttonNext, this._buttonPrev, this._buttonSubmit,
            ]);
        }

        this._palette.onAnimateInComplete();
        PubSub.publishSync(HLF.RE_MAP_KEYBOARD, undefined);
        this.createKeyboardMap();
    }

    protected onPaintPaletteColourChanged(): void {
        if (this._palette.activeColour !== undefined) {
            for (let i = 0; i < this._paintables.length; ++i) {
                this._paintables[i].activeColor = this._palette.activeColour!;
            }
            this.activePaintable.setInputEnabled(true);
        }
        else {
            this.activePaintable.setInputEnabled(false);
        }
    }

    protected onClearPressed(): void {
        this.activePaintable.clear();
    }

    protected switchPaintable(pStep: number): TimelineLite {
        this.setButtonsInteractive(false);
        this.setTouchControlsEnabled(false);
        this.resetZoom();

        PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
            HLF.ArrayUtils.random(SoundEffect.WHOOSH),
            1, false, HLF.AudioCategory.SFX),
        );

        const timeline: TimelineLite = new TimelineLite();
        const isIncrement: boolean = pStep > 0;
        timeline.add(this.animatePaintableOut(this._activePaintableIndex, isIncrement));
        this.incrementIndex(pStep);
        timeline.add(this.animatePaintableIn(this._activePaintableIndex, isIncrement), 0);
        timeline.call(() => {
            this.setButtonsInteractive(true);
            this.setTouchControlsEnabled(true);
            this._paintableSize.set(this.activePaintable.width, this.activePaintable.height);
        });
        return timeline;
    }

    protected animatePaintableIn(pIndex: number, pFromRight: boolean): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        const paintable: Paintable = this._paintables[pIndex];
        paintable.visible = true;
        paintable.setInputEnabled(false);
        paintable.position.x = (this._size.x * 0.5 + paintable.width * 0.5) * (pFromRight ? 1 : -1);
        timeline.to(paintable.position, 0.75, {
            x: 0,
            ease: Sine.easeInOut,
        });
        timeline.call(() => {
            if (this._palette.activeColour !== undefined) {
                paintable.setInputEnabled(true);
            }
            this.createKeyboardMap();
        });
        return timeline;
    }

    protected animatePaintableOut(pIndex: number, pToLeft: boolean): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        const paintable: Paintable = this._paintables[pIndex];
        paintable.setInputEnabled(false);
        timeline.to(paintable.position, 0.75, {
            x: (this._size.x * 0.5 + paintable.width * 0.5) * (pToLeft ? -1 : 1),
            ease: Sine.easeInOut,
        });
        timeline.eventCallback(Constants.TWEEN_ONCOMPLETE, () => {
            paintable.visible = false;
        });
        return timeline;
    }

    protected incrementIndex(pStep: number): void {
        this._activePaintableIndex += pStep;
        if (this._activePaintableIndex >= this._paintables.length) {
            this._activePaintableIndex = 0;
        }
        else if (this._activePaintableIndex < 0) {
            this._activePaintableIndex = this._paintables.length - 1;
        }
    }

    protected saveData(): void {
        for (let i = 0; i < this._paintables.length; ++i) {
            AppProgress.games[this._gameType].saveData[i] = this._paintables[i].generateSaveData();
        }
        AppProgress.games[this._gameType].recentItemIndex = this._activePaintableIndex;
    }

    protected onNextPressed(): void {
        this.switchPaintable(1);
    }

    protected onPrevPressed(): void {
        this.switchPaintable(-1);
    }

    protected onOnboardingComplete(): void {
        this._isOnboarding = false;

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._buttonClear, 0.5, {
            x: this._size.x * 0.5 - this._buttonClear.width * 0.5 - 15,
            ease: Sine.easeOut,
        }, 0);
        timeline.to(this._buttonNext, 0.5, {
            x: this._size.x * 0.5 - this._buttonNext.width * 0.5 - 15,
            ease: Sine.easeOut,
        }, 0.1);
        timeline.to(this._buttonPrev, 0.5, {
            x: this._size.x * 0.5 - this._buttonPrev.width * 0.5 - 15,
            ease: Sine.easeOut,
        }, 0.2);
        timeline.to(this._buttonSubmit, 0.5, {
            x: this._size.x * 0.5 - this._buttonSubmit.width * 0.5 - 15,
            ease: Sine.easeOut,
        }, 0.3);
        timeline.call(() => {
            this.setButtonsInteractive(true);
            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                this._buttonClear, this._buttonNext, this._buttonPrev, this._buttonSubmit,
            ]);
            this.createKeyboardMap();
        });
        if (this._zoomOnboarding.isActive) {
            this._zoomOnboarding.hide();
        }
    }

    protected createKeyboardMap(): void {
        // Regenerate the automatic map
        PubSub.publishSync(HLF.SET_KEYBOARD_AUTOMAP, true);
        PubSub.publishSync(HLF.RE_MAP_KEYBOARD, undefined);
        PubSub.publishSync(HLF.SET_KEYBOARD_AUTOMAP, false);

        // Separate screen into three sections for convenience
        const left: HLF.Input.IFocusable[] = new Array<HLF.Input.IFocusable>();
        left.push(this._palette.buttonPrev);
        left.push(...this._palette.buttonsPaintActive);
        left.push(this._palette.buttonNext);
        const center: HLF.Input.IFocusable[] = new Array<HLF.Input.IFocusable>();
        center.push(...this.activePaintable.layers);
        const right: HLF.Input.IFocusable[] = new Array<HLF.Input.IFocusable>();
        if (this._buttonSubmit.interactive === true) {
            right.push(this._buttonClear);
            right.push(this._buttonNext);
            right.push(this._buttonPrev);
            right.push(this._buttonSubmit);
        }

        // Overwrite specific links
        for (let i = 0; i < left.length; ++i) {
            PubSub.publishSync(HLF.KEYBOARD_FORCE_NEIGHBOURS, new HLF.Input.KeyboardMapToken(
                left[i],
                i === 0 ? left[i] : left[i - 1],
                i >= left.length - 1 ? left[i] : left[i + 1],
                left[i],
                center[0],
            ));
        }
        for (let i = 0; i < center.length; ++i) {
            PubSub.publishSync(HLF.KEYBOARD_FORCE_NEIGHBOURS, new HLF.Input.KeyboardMapToken(
                center[i],
                i > 0 ? center[i - 1] : center[center.length - 1],
                i < center.length - 1 ? center[i + 1] : center[0],
                this._palette.buttonsPaintActive[0],
                this._isOnboarding ? center[i] : this._buttonSubmit,
            ));
        }
        for (let i = 0; i < right.length; ++i) {
            PubSub.publishSync(HLF.KEYBOARD_FORCE_NEIGHBOURS, new HLF.Input.KeyboardMapToken(
                right[i],
                i === 0 ? right[i] : right[i - 1],
                i >= right.length - 1 ? right[i] : right[i + 1],
                center[0],
                right[i],
            ));
        }
    }

    protected logHitArea(): void {
        let map: number[][];
        let tempMap: boolean[][];
        let tempMapPair: boolean[][] | undefined;
        let width: number;
        let height: number;
        let log: string;
        let layerCount: number;
        const isDoll: boolean = (this._gameType === GameType.DOLL);
        for (let i = 0; i < this._paintables.length; ++i) {
            // tslint:disable-next-line:no-string-literal
            tempMap = this._paintables[i]["_layers"][0]["hitArea"]["_map"];
            width = tempMap.length;
            height = tempMap[0].length;
            map = new Array<number[]>(width);
            for (let x = 0; x < width; ++x) {
                map[x] = new Array<number>(height);
                for (let y = 0; y < height; ++y) {
                    map[x][y] = -1;
                }
            }

            // tslint:disable-next-line:no-string-literal
            layerCount = this._paintables[i]["_layers"].length * (isDoll ? 0.5 : 1);
            for (let j = 0; j < layerCount; ++j) {
                // tslint:disable-next-line:no-string-literal
                tempMap = this._paintables[i]["_layers"][j]["hitArea"]["_map"];
                // tslint:disable-next-line:no-string-literal
                tempMapPair = isDoll ? this._paintables[i]["_layers"][j + layerCount]["hitArea"]["_map"] : undefined;
                for (let x = 0; x < width; ++x) {
                    for (let y = 0; y < height; ++y) {
                        if (tempMap[x][y] || (tempMapPair !== undefined && tempMapPair[x][y])) {
                            map[x][y] = j;
                        }
                    }
                }
            }
            log = "[\n";
            for (let x = 0; x < width; ++x) {
                log = log + "\t[ ";
                for (let y = 0; y < height; ++y) {
                    log = log + map[x][y] + ", ";
                }
                log = log + "],\n";
            }
            log = log + "],";
            console.log(log);
        }
    }

    private populateOnboarding(): void {
        const isMobile: boolean = PIXI.utils.isMobile.any;
        this._onboardingManager.clear();
        this._onboardingManager.addStep(
            Topics.ACTIVE_PAINT_SELECTED,
            undefined,
            this._palette,
            5.0,
            true,
        );
        this._onboardingManager.addStep(
            Topics.PAINTABLE_LAYER_PAINTED,
            undefined,
            this.activePaintable,
            2.0,
            true,
            Topics.ACTIVE_PAINT_DESELECTED,
            undefined,
            () => {
                if (PIXI.utils.isMobile.any === false || this._hasZoomed === true) {
                    this.onOnboardingComplete();
                }
            },
        );
        if (isMobile) {
            this._onboardingManager.addStep(
                Topics.BEGIN_ZOOM,
                undefined,
                undefined,
                3.0,
                false,
                undefined,
                () => { this._zoomOnboarding.show(); },
                this.onOnboardingComplete.bind(this),
            );
        }
        this._onboardingManager.start();
    }

    private setTouchControlsEnabled(pEnabled: boolean): void {
        this._touchManager.onSwipe = pEnabled ? this.onSwipe.bind(this) : undefined;
        this._touchManager.onPinch = pEnabled ? this.onPinch.bind(this) : undefined;
    }

    private resetZoom(pInstant: boolean = false): void {
        const resetTimeline: TimelineLite = new TimelineLite();
        const tweenTime: number = (pInstant ? 0 : 0.5 * (this.activePaintable.scale.x - 1));
        resetTimeline.to(this.activePaintable.scale, tweenTime, {
            x: 1,
            y: 1,
            ease: Linear.easeNone,
        });
        resetTimeline.to(this.activePaintable.position, tweenTime, {
            x: 0,
            y: 0,
            ease: Linear.easeNone,
        }, 0);
        this._pinchDelta = 0;
        this._zoomPerc = 0;
    }

    private setButtonsInteractive(pInteractive: boolean): void {
        this._buttonClear.interactive = pInteractive;
        this._buttonNext.interactive = pInteractive;
        this._buttonPrev.interactive = pInteractive;
        this._buttonSubmit.interactive = pInteractive;
    }

    private onTouchStart(): void {
        this.activePaintable.interactiveChildren = false;
        this.setButtonsInteractive(false);
        this._palette.interactiveChildren = false;
    }

    private onTouchEnd(): void {
        this.activePaintable.interactiveChildren = true;
        this.setButtonsInteractive(true);
        this._palette.interactiveChildren = true;
    }

    private onSwipe(pDelta: PIXI.Point): void {
        HLF.PointUtils.addToPoint(this.activePaintable.position, pDelta);
        this.clampPaintablePosition();
    }

    private onPinch(pDelta: number): void {
        this._pinchDelta = HLF.MathUtils.clamp(this._pinchDelta + pDelta, 0, GamePaintable.PINCH_MAX_DELTA);
        this._zoomPerc = (this._pinchDelta / GamePaintable.PINCH_MAX_DELTA);
        this.activePaintable.scale.set(1 + this._zoomPerc * (GamePaintable.MAX_SCALE - 1));
        this.clampPaintablePosition();
        if (pDelta > 0) {
            this._hasZoomed = true;
            PubSub.publishSync(Topics.BEGIN_ZOOM, undefined);
        }
    }

    private clampPaintablePosition(): void {
        this.activePaintable.x = HLF.MathUtils.clamp(
            this.activePaintable.x,
            this._zoomPerc * (GamePaintable.MAX_SCALE - 1) * 0.5 * -this._paintableSize.x,
            this._zoomPerc * (GamePaintable.MAX_SCALE - 1) * 0.5 * this._paintableSize.x,
        );
        this.activePaintable.y = HLF.MathUtils.clamp(
            this.activePaintable.y,
            this._zoomPerc * (GamePaintable.MAX_SCALE - 1) * 0.5 * -this._paintableSize.y,
            this._zoomPerc * (GamePaintable.MAX_SCALE - 1) * 0.5 * this._paintableSize.y,
        );
    }
}
